/**
 * Authentication state for the whole SPA.
 *
 * Model: JWTs live in localStorage ('access_token' / 'refresh_token');
 * on mount this provider calls GET /api/auth/me to turn a stored token
 * into a user object. `user.isStaff` is derived from the backend's
 * user_type field and is the flag App.js uses to gate every view — the
 * system is staff-only, so a non-staff session sees the login screen.
 *
 * The only login flow handled here:
 *   - Staff: loginWithMicrosoft() fetches an Azure auth URL from the
 *     backend and navigates away; Azure redirects back to the app with
 *     a single-use ?code=… in the query string. The init effect POSTs it
 *     to /api/auth/staff/exchange, stores the returned tokens and scrubs
 *     the URL bar — the JWTs themselves never appear in a URL.
 *   (The client email/password flow was retired with the insurance
 *   claims domain.)
 *
 * Gotchas:
 *   - Tokens in localStorage are readable by any JS on the origin (XSS
 *     would exfiltrate them); the backend chose this over httpOnly
 *     cookies.
 *   - There is NO refresh logic — an expired access token just fails on
 *     the next apiFetch, which clears storage and reloads to '/' (see
 *     utils/api.js). The stored refresh_token is never used anywhere in
 *     this codebase.
 *
 * Permissions: once a user is authenticated this provider also fetches
 * GET /api/permissions/me — the effective app×capability grid the
 * backend will enforce. The context exposes `permissions`,
 * `isAdministrator`, `permissionsLoading` and a `can(app, capability)`
 * helper so pages can hide what the user may not open. This is
 * convenience only — the API enforces every check itself — and it
 * FAILS CLOSED: if the fetch errors or the grid is absent, can()
 * returns false rather than guessing permissive.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

const AuthContext = createContext();

// Empty string → same-origin requests (production build served by
// FastAPI); set REACT_APP_BACKEND_URL in dev when the API is on :8000
// and the dev server on :3000.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]                   = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [authError, setAuthError]         = useState(null);
  // null = not fetched or fetch failed; can() treats null as "no
  // permissions" so a failed lookup never silently opens the UI.
  const [permissions, setPermissions]     = useState(null);
  const [isAdministrator, setIsAdministrator] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // ── Fetch the effective permission grid for the current user ─────────
  // Called right after /api/auth/me succeeds (apiFetch attaches the stored
  // token itself). Errors leave permissions=null — fail closed.
  const fetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const res = await apiFetch('/api/permissions/me');
      if (res && res.ok) {
        const data = await res.json();
        setPermissions(data.permissions || {});
        setIsAdministrator(!!data.is_administrator);
      } else {
        setPermissions(null);
        setIsAdministrator(false);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setPermissions(null);
      setIsAdministrator(false);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  // ── Fetch /api/auth/me with a stored token ─────────────────────────────
  const fetchCurrentUser = useCallback(async (token) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // isStaff is a client-side convenience flag only — real
        // authorization is enforced server-side on every endpoint.
        setUser({ ...data, isStaff: data.user_type === 'staff' });
        setIsAuthenticated(true);
        fetchPermissions();
        return true;
      }
      // Token rejected — clear storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return false;
    } catch (err) {
      console.error('Failed to fetch user:', err);
      return false;
    }
  }, [fetchPermissions]);

  // ── Client-side permission predicate — UI convenience only ──────────
  // Real enforcement is in backend require_route_permission; this just
  // decides what to render. Absent grid → false (fail closed).
  const can = useCallback((app, capability) => {
    return !!permissions?.[app]?.[capability];
  }, [permissions]);

  // ── On mount: handle OAuth callback or restore existing session ────────
  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const exchangeCode  = params.get('code');
      const callbackError = params.get('error');

      if (callbackError) {
        // The backend can only send machine-readable codes through the
        // OAuth redirect URL, so translate the known ones into something a
        // human can act on. Unknown codes pass through unchanged.
        const readableErrors = {
          not_authorised: 'Your account is not a member of a VetFlow access group. Please contact your administrator.',
          group_claim_missing: 'Sign-in is not fully configured — group information is missing from your account. Please contact your administrator.',
          service_unavailable: 'VetFlow is temporarily unavailable. Please try again shortly.',
          authentication_failed: 'Sign-in failed. Please try again.',
        };
        setAuthError(readableErrors[callbackError] || callbackError);
        window.history.replaceState({}, '', '/');
        setLoading(false);
        return;
      }

      if (exchangeCode) {
        // Coming back from Microsoft login — the URL carries only a
        // single-use exchange code; redeem it for the real tokens, then
        // scrub the URL. The code expires in 60s and is deleted on first
        // use, so a refresh of this page cannot re-redeem it.
        window.history.replaceState({}, '', '/');
        try {
          const res = await fetch(`${BACKEND_URL}/api/auth/staff/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: exchangeCode }),
          });
          if (res.ok) {
            const tokens = await res.json();
            localStorage.setItem('access_token', tokens.access_token);
            if (tokens.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token);
            await fetchCurrentUser(tokens.access_token);
          } else {
            setAuthError('Sign-in could not be completed. Please try again.');
          }
        } catch (err) {
          console.error('Token exchange failed:', err);
          setAuthError('Sign-in could not be completed. Please try again.');
        }
        setLoading(false);
        return;
      }

      // Restore existing session
      const stored = localStorage.getItem('access_token');
      if (stored) {
        await fetchCurrentUser(stored);
      }
      setLoading(false);
    };

    init();
  }, [fetchCurrentUser]);

  // ── Redirect to Microsoft login ────────────────────────────────────────
  const loginWithMicrosoft = async () => {
    try {
      setAuthError(null);
      const res  = await fetch(`${BACKEND_URL}/api/auth/staff/login`);
      const data = await res.json();
      window.location.href = data.auth_url;
    } catch (err) {
      console.error('Failed to initiate Microsoft login:', err);
      setAuthError('Could not reach the authentication service.');
    }
  };

  // ── Return the stored access token (for API calls) ─────────────────────
  const getToken = () => localStorage.getItem('access_token');

  // ── Logout ─────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    setAuthError(null);
    setPermissions(null);
    setIsAdministrator(false);
    setPermissionsLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      authError,
      loginWithMicrosoft,
      logout,
      getToken,
      permissions,
      isAdministrator,
      permissionsLoading,
      can,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
